/**
 * On-demand manual version comparison (ADR-063).
 *
 * The editor used to render {@link DiffView} in an always-visible sidebar
 * panel. The captain's decision (ADR-062) keeps the capability but relocates
 * it behind an explicit "Compare versions" control: this is that control's
 * modal, opened once two versions are selected in {@link VersionsDropdown}.
 * It is a thin presentational wrapper — the diff data/loading plumbing
 * (`useDocumentVersions.loadDiff`) is unchanged, so this never re-implements
 * diffing, only where it is shown.
 */
import React, { useEffect } from "react";

import DiffView from "./DiffView";
import type { VersionDiff } from "../../types";

/** Props for {@link CompareVersionsDialog}. */
export interface CompareVersionsDialogProps {
  /** The diff to render, or null while none is selected/loaded. */
  diff: VersionDiff | null;
  /** `{ source id: title }` so inline citations resolve to human labels. */
  sourceTitles?: Record<string, string> | null;
  /** Close the dialog. */
  onClose: () => void;
}

/**
 * Render the on-demand "Compare versions" modal.
 *
 * @param props - The diff to render and a close handler.
 * @returns The dialog element.
 */
export const CompareVersionsDialog: React.FC<CompareVersionsDialogProps> = ({
  diff,
  sourceTitles,
  onClose,
}) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      data-testid="compare-versions-dialog"
      role="dialog"
      aria-modal="true"
      aria-label="Compare versions"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-6"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-xl bg-white p-5 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-3 flex shrink-0 items-center justify-between border-b border-slate-100 pb-3">
          <h3 className="text-sm font-bold text-slate-800">Compare versions</h3>
          <button
            type="button"
            data-testid="close-compare-versions"
            onClick={onClose}
            aria-label="Close compare versions"
            className="rounded px-2 py-1 text-sm font-medium text-slate-500 hover:bg-slate-100"
          >
            Close
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <DiffView diff={diff} sourceTitles={sourceTitles} />
        </div>
      </div>
    </div>
  );
};

export default CompareVersionsDialog;
